import { type Patient } from '@avanta/shared/types/patients';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useFetchEnterprise } from 'hooks/useFetchEnterprise';
import { useUserStore } from 'stores/user';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { type MessageType, QUESTION_TYPES } from 'types/sapa';
import { questions } from '../utils/questionFlow';
import { QUESTION_IDS, TIMEOUTS } from './constants';
import { type Question } from './types';
import {
  createUserMessage,
  createSystemMessage,
  formatSelectedOptions,
  formatUserResponse,
  extractAnswerForNextQuestion,
  determineNextFlow,
} from './utils';

export const useSapaChat = (): {
  messages: MessageType[];
  currentQuestionId: string;
  handleAnswer: (questionId: string, answer: unknown, textInput?: string) => void;
  answers: Record<string, unknown>;
  isFinished: boolean;
  isProcessing: boolean;
} => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [currentQuestionId, setCurrentQuestionId] = useState<string>(QUESTION_IDS.WELCOME);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [isFinished, setIsFinished] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const initialized = useRef(false);
  const patient = useUserStore(state => state.user);
  const { trackSapaFormSubmitted } = useTrackingEvents();
  const { data: enterprise } = useFetchEnterprise({
    enterpriseId: patient?.enterpriseId,
  });
  const addMessage = useCallback((message: MessageType): void => {
    setMessages(prev => [...prev, message]);
  }, []);
  useEffect(() => {
    if (!initialized.current) {
      const welcomeQuestion = questions[QUESTION_IDS.WELCOME] as Question;
      addMessage(createSystemMessage(welcomeQuestion));
      initialized.current = true;
    }
  }, [addMessage]);
  const handleAnswer = useCallback(
    (questionId: string, answer: unknown, textInput?: string): void => {
      setIsProcessing(true);
      const answerWithText = textInput ? { answer, textInput } : answer;
      setAnswers(prev => ({ ...prev, [questionId]: answerWithText }));
      const question = questions[questionId] as Question;
      const isSelectableQuestion =
        question.type === QUESTION_TYPES.SINGLE || question.type === QUESTION_TYPES.MULTIPLE;
      const isConclusionTextOnly =
        question.id === QUESTION_IDS.CONCLUSION &&
        (!question.options || question.options.length === 0) &&
        question.hasTextInput;

      if (isSelectableQuestion && !isConclusionTextOnly) {
        const selectedOptions = formatSelectedOptions(question.options, answer);
        const content = formatUserResponse(selectedOptions, textInput);
        addMessage(createUserMessage(content));
      }
      const answerForNextQuestion = extractAnswerForNextQuestion(answer, question.type);
      const nextId =
        typeof question.nextQuestion === 'function'
          ? question.nextQuestion(answerForNextQuestion)
          : question.nextQuestion;
      determineNextFlow({
        nextId,
        navigate,
        setIsFinished,
        setCurrentQuestionId,
        addMessage,
        answers,
        questionId,
        answerWithText,
        user: patient as Patient,
        companyName: enterprise?.name,
        trackSapaFormSubmitted,
      });
      setTimeout(() => {
        setIsProcessing(false);
      }, TIMEOUTS.MESSAGE_DELAY);
    },
    [answers, navigate, patient, addMessage, enterprise?.name, trackSapaFormSubmitted]
  );

  return {
    messages,
    currentQuestionId,
    handleAnswer,
    answers,
    isFinished,
    isProcessing,
  };
};
